import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import Carousel from 'LearningManager/components/Carousel';
import CourseCard from 'LearningManager/components/CourseCard';
import Heading from 'LearningManager/components/Heading';
import {
	PRIORITY_OPTIONS,
	STATUS_OPTIONS,
	TYPE_OPTIONS,
	CATEGORY_OPTIONS,
	CourseData,
	LearningPathData
} from 'LearningManager/constants/UserDashboard.constants';
import LearningPathCard from 'LearningManager/components/LearningPathCard';
import { formatCardDate } from 'LearningManager/helpers/date';
import { Icon } from 'Components';
import { useStores } from 'Hooks/useStore';
import {
	ActivityAssignment,
	ActivityAssignmentStatus,
	LearningPathAssignment
} from 'Services/Api/UserDashboard/Types';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import styles from './TrainingSection.module.scss';

const PLACEHOLDER_IMAGES = [course1, course2, course3, course4];

const STATUS_TAG: Record<ActivityAssignmentStatus, string> = {
	completed: 'Complete',
	in_progress: 'In Progress',
	not_started: 'Not started'
};

const formatDueDate = (dueDate?: string): string => {
	const formatted = formatCardDate(dueDate);
	return formatted ? `Due ${formatted}` : '';
};

const toCourseData = (
	activity: ActivityAssignment,
	imageIndex: number
): CourseData => ({
	id: activity.id,
	title: activity.name,
	meta: formatDueDate(activity.due_date),
	tags: [
		STATUS_TAG[activity.status],
		activity.is_required ? 'Required' : 'Optional'
	],
	image: PLACEHOLDER_IMAGES[imageIndex % PLACEHOLDER_IMAGES.length]
});

const toLearningPathData = (
	lp: LearningPathAssignment,
	activitiesById: Record<number, ActivityAssignment>
): LearningPathData => {
	const orderedActivities = [...lp.activities].sort(
		(a, b) => a.order - b.order
	);

	const courses = orderedActivities.map((ref, idx) => {
		const activity = activitiesById[ref.id];
		return activity
			? toCourseData(activity, idx)
			: {
					id: ref.id,
					title: `Activity ${ref.id}`,
					meta: '',
					tags: [ref.is_required ? 'Required' : 'Optional'],
					image: PLACEHOLDER_IMAGES[idx % PLACEHOLDER_IMAGES.length]
			  };
	});

	const courseCount = lp.activities.length;
	const completedCount = lp.activities.filter(
		(ref) => activitiesById[ref.id]?.status === 'completed'
	).length;
	const completionPercentage = courseCount
		? Math.round((completedCount / courseCount) * 100)
		: 0;

	const statuses: string[] = [];
	if (lp.is_required) statuses.push('Required');
	if (courseCount > 0 && completedCount === courseCount) {
		statuses.push('Complete');
	} else if (completedCount > 0) {
		statuses.push('In Progress');
	}

	return {
		id: `lp-${lp.id}`,
		title: lp.name,
		statuses,
		courseCount,
		completedCount,
		completionPercentage,
		courses
	};
};

const TrainingSection: React.FC = observer(() => {
	const { userDashboardStore } = useStores();
	const {
		learningPathAssignments,
		activityAssignments,
		activityAssignmentsById
	} = userDashboardStore;

	const launch = (activityId: number) => {
		const activity = activityAssignmentsById[activityId];
		if (activity) userDashboardStore.launchActivity(activity);
	};

	const ctaLabel = (status: ActivityAssignmentStatus) =>
		status === 'completed' ? 'Review' : 'Start';

	const learningPaths = useMemo(
		() =>
			learningPathAssignments.map((lp) =>
				toLearningPathData(lp, activityAssignmentsById)
			),
		[learningPathAssignments, activityAssignmentsById]
	);

	// Courses section shows standalone activities (not part of any learning path)
	const standaloneCourses = useMemo(() => {
		const lpActivityIds = new Set(
			learningPathAssignments.flatMap((lp) =>
				lp.activities.map((a) => a.id)
			)
		);
		return activityAssignments.filter((a) => !lpActivityIds.has(a.id));
	}, [learningPathAssignments, activityAssignments]);

	return (
		<div>
			<div className={styles.header}>
				<span className={styles.title}>Training</span>
				<div className={styles.filters}>
					<MultiSelectDropdown
						placeholder="Priority (All)"
						options={PRIORITY_OPTIONS}
						className={styles.filter}
					/>
					<MultiSelectDropdown
						placeholder="All states"
						options={STATUS_OPTIONS}
						className={styles.filter}
					/>
					<MultiSelectDropdown
						placeholder="All types"
						options={TYPE_OPTIONS}
						className={styles.filter}
					/>
					<MultiSelectDropdown
						placeholder="All categories"
						options={CATEGORY_OPTIONS}
						className={styles.filter}
					/>
				</div>
			</div>

			<div className={styles.section}>
				<Heading
					icon={
						<Icon className={styles.icon} type="darkLP" role="img" />
					}
					title="Learning Paths"
				/>
				<div className={styles.learningPathsWrapper}>
					{learningPaths.length ? (
						learningPaths.map((lp) => (
							<LearningPathCard
								key={lp.id}
								learningPath={lp}
								onCourseClick={(course) => launch(course.id)}
							/>
						))
					) : (
						<div className={styles.emptyMessage}>
							You have no learning paths assigned to you yet.
						</div>
					)}
				</div>
			</div>

			<div className={styles.section}>
				<Heading
					icon={
						<Icon
							className={styles.icon}
							type="darkIndiCourses"
							role="img"
						/>
					}
					title="Courses"
				/>
				<div className={styles.coursesWrapper}>
					{!standaloneCourses.length && (
						<div className={styles.emptyMessage}>
							You have no courses assigned to you yet.
						</div>
					)}
					{!!standaloneCourses.length && (
						<Carousel
							data={standaloneCourses.map((activity, idx) =>
								toCourseData(activity, idx)
							)}
							itemsPerPage={4}
							renderItem={(course, index) => {
								const activity =
									activityAssignmentsById[course.id];
								const showReview =
									activity?.status === 'completed';
								return (
									<CourseCard
										index={index}
										htmlId={`lms-ud-course-${course.id}`}
										course={course}
										onClick={() => launch(course.id)}
										ctaLabel={
											activity
												? ctaLabel(activity.status)
												: 'Start'
										}
										ctaVariant={
											showReview ? 'border' : undefined
										}
										ctaClassName={
											showReview
												? styles.reviewBtn
												: undefined
										}
										hideBadge
										hideRequiredSection
									/>
								);
							}}
						/>
					)}
				</div>
			</div>
		</div>
	);
});

export default TrainingSection;
